/**
 * Streaming polyphase windowed-sinc resampler.
 *
 * Replaces per-frame linear interpolation on the raw-mic path, which had
 * two fidelity problems:
 *   - No anti-aliasing low-pass before decimation. At the common 48 kHz →
 *     16 kHz ratio the interpolation degenerated into picking every 3rd
 *     sample, folding all 8–24 kHz energy into the speech band.
 *   - Stateless per-frame operation. The fractional read position was
 *     discarded at every frame boundary, so sources that deliver odd
 *     frame sizes (Bluetooth headsets are the documented offender — HFP
 *     renegotiation and A2DP route changes produce non-10 ms frames and
 *     rates of 8/16/24/44.1 kHz) accumulated drift and boundary ticks.
 *
 * This implementation:
 *   - Applies a Kaiser-windowed sinc low-pass (cut below the lower of the
 *     two Nyquist frequencies) as part of the resample, so decimation is
 *     properly band-limited and upsampling (8 kHz HFP mic → 16 kHz) is
 *     properly imaged.
 *   - Carries filter history and the read position across `process()`
 *     calls. The position advances in exact rational arithmetic
 *     (inputRate/outputRate reduced to integers), so output is
 *     bit-identical whether the stream is processed in one buffer or in
 *     arbitrary odd-sized chunks, and phase can never drift — even over
 *     unbounded streams at non-integer ratios like 44.1 kHz → 16 kHz.
 *   - Precomputes a polyphase coefficient table once per rate pair and
 *     linearly interpolates between adjacent phase rows, so the per-sample
 *     cost is a short FIR dot product (no trig in the hot path). At
 *     48 kHz → 16 kHz that is ~4M multiply-adds per second — negligible.
 *
 * Latency: KERNEL_HALF_WIDTH input samples (4/3 ms at 48 kHz). The tail
 * inside the kernel window when the stream ends is dropped (< 1 ms of
 * audio); irrelevant for continuous capture.
 *
 * Framework-free on purpose (no DOM, no Electron) — unit-testable in
 * isolation, matching the conventions of this directory.
 */

/** Kernel half-width in input samples; total taps = 2x this. 128 taps
 *  at 48 kHz → ~2.1 kHz transition band with the Kaiser window below,
 *  so with the cutoff at 0.94·Nyquist the passband stays flat through
 *  ~7.2 kHz (measured −1 dB ≈ 7.3 kHz) while 10 kHz aliases still land
 *  below −60 dB. The previous 64-tap / 0.9-cutoff kernel rolled off
 *  from ~6 kHz (−3 dB at 6.8 kHz), audibly dulling the top of the
 *  speech band. Still only ~4M multiply-adds/s at 48 kHz → 16 kHz. */
const KERNEL_HALF_WIDTH = 64
const TAPS = KERNEL_HALF_WIDTH * 2
/** Phase resolution of the polyphase table. Adjacent rows are linearly
 *  interpolated, so this bounds table size, not phase accuracy. */
const PHASES = 32
/** Kaiser window shape parameter (~90 dB sidelobes). */
const KAISER_BETA = 9
/** Low-pass cutoff as a fraction of the lower Nyquist. Leaves headroom
 *  for the filter's transition band to stay below Nyquist. */
const CUTOFF_RATIO = 0.94

/** Zeroth-order modified Bessel function of the first kind (power
 *  series). Only used at kernel-build time. */
function besselI0(x: number): number {
  let sum = 1
  let term = 1
  for (let k = 1; k < 40; k++) {
    term *= (x * x) / (4 * k * k)
    sum += term
    if (term < sum * 1e-14) {
      break
    }
  }
  return sum
}

/** Build the (PHASES + 1) × TAPS polyphase coefficient table for a
 *  Kaiser-windowed sinc low-pass cut at CUTOFF_RATIO of the lower
 *  Nyquist. Each row is normalised to unity DC gain so amplitude is
 *  identical across phases. Row layout is row-major. */
function buildPolyphaseKernel(inputRate: number, outputRate: number): Float64Array {
  // Cutoff in cycles per INPUT sample. min() handles both directions:
  // downsampling band-limits below the output Nyquist, upsampling below
  // the input Nyquist (anti-imaging).
  const cutoff = (CUTOFF_RATIO * (Math.min(inputRate, outputRate) / 2)) / inputRate
  const windowNorm = besselI0(KAISER_BETA)
  const kernel = new Float64Array((PHASES + 1) * TAPS)

  for (let p = 0; p <= PHASES; p++) {
    const frac = p / PHASES
    const row = p * TAPS
    let sum = 0
    for (let k = 0; k < TAPS; k++) {
      // Kernel argument: distance from tap sample to the (fractional)
      // output position, in input samples. See process() for the
      // matching tap window.
      const t = k - (KERNEL_HALF_WIDTH - 1) - frac
      const u = t / KERNEL_HALF_WIDTH
      let v = 0
      if (Math.abs(u) <= 1) {
        const window = besselI0(KAISER_BETA * Math.sqrt(1 - u * u)) / windowNorm
        const x = 2 * cutoff * t
        const sinc = x === 0 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x)
        v = 2 * cutoff * sinc * window
      }
      kernel[row + k] = v
      sum += v
    }
    for (let k = 0; k < TAPS; k++) {
      kernel[row + k] /= sum
    }
  }
  return kernel
}

function gcd(a: number, b: number): number {
  while (b !== 0) {
    const t = a % b
    a = b
    b = t
  }
  return a
}

export class StreamingResampler {
  readonly inputRate: number
  readonly outputRate: number

  private readonly kernel: Float64Array
  /** Read-position step per output sample as an exact rational:
   *  stepNum / fracDen input samples (inputRate/outputRate reduced).
   *  Integer arithmetic keeps the resample phase exact forever — no
   *  accumulated float error, so output is bit-identical for any
   *  chunking and can never drift, even over unbounded streams. */
  private readonly stepNum: number
  private readonly fracDen: number
  /** Carried input samples: unconsumed tail of the previous call plus
   *  the kernel-history samples the next output still needs. */
  private pending: Float32Array
  /** Read position within `pending` for the next output sample:
   *  posInt + posFracNum / fracDen. The fractional part is the
   *  resample phase; carried across calls so chunk boundaries are
   *  seamless at any frame size. */
  private posInt = KERNEL_HALF_WIDTH
  private posFracNum = 0

  constructor(inputRate: number, outputRate: number) {
    if (!Number.isFinite(inputRate) || !Number.isFinite(outputRate) || inputRate <= 0 || outputRate <= 0) {
      throw new Error(`StreamingResampler: invalid rates ${inputRate} → ${outputRate}`)
    }
    if (inputRate === outputRate) {
      throw new Error('StreamingResampler: equal rates need no resampler — bypass instead')
    }
    this.inputRate = inputRate
    this.outputRate = outputRate
    const g = gcd(Math.round(inputRate), Math.round(outputRate))
    this.stepNum = Math.round(inputRate) / g
    this.fracDen = Math.round(outputRate) / g
    this.kernel = buildPolyphaseKernel(inputRate, outputRate)
    this.pending = this.initialPending()
  }

  /** Drop all carried stream state (history + phase), keeping the
   *  kernel. Call when stream continuity breaks (capture restart,
   *  pause/resume) so stale samples aren't spliced across the gap. */
  resetState(): void {
    this.pending = this.initialPending()
    this.posInt = KERNEL_HALF_WIDTH
    this.posFracNum = 0
  }

  /** Kernel-half-width of leading silence so the first real sample has
   *  full kernel support (constant sub-millisecond latency instead of
   *  an edge-clamped, distorted stream head). */
  private initialPending(): Float32Array {
    return new Float32Array(KERNEL_HALF_WIDTH)
  }

  /** Resample the next chunk of the stream. Returns every output sample
   *  whose kernel window is fully covered by the samples seen so far;
   *  the remainder is carried into the next call. */
  process(input: Float32Array): Float32Array {
    const H = KERNEL_HALF_WIDTH
    const merged = new Float32Array(this.pending.length + input.length)
    merged.set(this.pending)
    merged.set(input, this.pending.length)

    // Output at position posInt + posFracNum/fracDen needs input indices
    // posInt - H + 1 … posInt + H, so the largest producible integer
    // part is merged.length - 1 - H. Count outputs by stepping the
    // rational position exactly.
    const lastProducible = merged.length - 1 - H
    const den = this.fracDen
    const stepInt = Math.floor(this.stepNum / den)
    const stepFracNum = this.stepNum - stepInt * den
    let outCount = 0
    if (this.posInt <= lastProducible) {
      // Whole steps that fit, computed in exact integer space.
      const room = (lastProducible - this.posInt) * den + (den - 1 - this.posFracNum)
      outCount = 1 + Math.floor(room / this.stepNum)
    }
    const out = new Float32Array(outCount)
    const kernel = this.kernel

    let posInt = this.posInt
    let posFracNum = this.posFracNum
    for (let n = 0; n < outCount; n++) {
      const phasePos = (posFracNum / den) * PHASES
      const p = Math.floor(phasePos)
      const phaseFrac = phasePos - p
      const base = posInt - H + 1
      const row0 = p * TAPS
      let acc = 0
      if (phaseFrac === 0) {
        for (let k = 0; k < TAPS; k++) {
          acc += merged[base + k] * kernel[row0 + k]
        }
      } else {
        const row1 = row0 + TAPS
        const w0 = 1 - phaseFrac
        for (let k = 0; k < TAPS; k++) {
          acc += merged[base + k] * (kernel[row0 + k] * w0 + kernel[row1 + k] * phaseFrac)
        }
      }
      out[n] = acc

      posInt += stepInt
      posFracNum += stepFracNum
      if (posFracNum >= den) {
        posFracNum -= den
        posInt += 1
      }
    }
    this.posInt = posInt
    this.posFracNum = posFracNum

    // Compact: drop samples no future output can reference.
    const keepFrom = Math.max(0, this.posInt - (H - 1))
    this.pending = merged.slice(keepFrom)
    this.posInt -= keepFrom
    return out
  }
}
